import React from "react"
import { GatsbyLinkProps, withPrefix } from "gatsby"
import Tippy from "@tippyjs/react"
import { MDXProvider } from "@mdx-js/react"
import MDXRenderer from "./mdx-renderer"
import { LinkToStacked } from "react-stacked-pages-hook"
import {
  hashtagTitlePrefix,
  roamBlockEmbedTitlePrefix,
  roamBlockTitlePrefix,
} from "gatsby-remark-double-parenthesis-link"

import "./anchor-tag.css"
import { Block, Common } from "../css-classes"

interface OutboundReference {
  mdx: string,
  title: string,
  id: string,
  displayTitle: string,
  slug: string,
}

interface AnchorTagProps extends GatsbyLinkProps<any> {
  title: string
  href?: string
  references: Array<OutboundReference>
  withoutLink: boolean
  withoutPopup: boolean
}

export const AnchorTag = (
  {
    title,
    href,
    references = [],
    withoutLink,
    withoutPopup,
    ...restProps
  }: AnchorTagProps) => {
  const ref = references.find((x) => withPrefix(x.slug) === withPrefix(href))

  let content = restProps.children
  if (withoutLink) {
    return <span>{content}</span>
  }

  let popupContent
  let child

  function blockEmbedClass() {
    return title?.startsWith(roamBlockEmbedTitlePrefix) ? ` ${Block.embed}` : ""
  }

  if (ref) {
    /**
     * This is IMPORTANT. In connection with short circuit above
     * this prevents the infinite rendering recursion for block references
     * referring to themselves
     */
    const nestedComponents = {
      a(props: AnchorTagProps) {
        return <AnchorTag {...props} references={references} withoutLink />
      },
      p(props) {
        return <span {...props} />
      },
    }

    const refContent = () => <div className="reference-content">
      <MDXProvider components={nestedComponents}>
        <MDXRenderer>{ref.mdx}</MDXRenderer>
      </MDXProvider>
    </div>

    // current hypothesis of what this means
    let linkToLocalPage = ref.title === ref.displayTitle

    popupContent = (
      <div id={ref.id} className={"popover with-markdown" + blockEmbedClass()}>
        <React.Fragment>
          {!linkToLocalPage && <h5>{ref.displayTitle}</h5>}
          {refContent()}
        </React.Fragment>
        <div className="more-content-blind" />
      </div>
    )

    let className = Common.link

    // todo a better way?
    if (title?.startsWith(roamBlockTitlePrefix)) {
      content = <React.Fragment>
        {refContent()}
      </React.Fragment>

      className = Block.reference + blockEmbedClass()
    }
    if (title?.startsWith(hashtagTitlePrefix)) {
      className = Common.hashtagLink
    }

    child = (
      <LinkToStacked className={className} {...restProps} to={ref.slug} title={title}>
        {content}
      </LinkToStacked>
    )
  } else {
    popupContent = <div className="popover no-max-width">{href}</div>
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    child = (
      <a
        {...restProps}
        href={(!href || (href?.indexOf("#") === 0)) ? href : withPrefix(href)}
        title={title}
      />
    )
  }

  if (withoutPopup) {
    return child
  }

  return (
    <Tippy animation="shift-away" content={popupContent} maxWidth="none">
      {child}
    </Tippy>
  )
}
